import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { api } from "../../Config/Api";
import { CategoriesType } from "../../types/categoriesType";
import { RootState } from "../store";
import { AppType } from "types/appType";
import { ErrorType } from "types/errorType";
import { ExtractErrorInfo } from "../../components/shared/ExtractErrorInfo";
import { API_ENDPOINTS } from "Config/apiConfig";

export interface AppTypeWithCategoriesType extends AppType {
  loading: boolean;
  error: ErrorType | null;
  categoriesType: CategoriesType;
}

const initialState: AppTypeWithCategoriesType = {
  error: null,
  loading: false,
  categoriesType: {
    id: "",
    name: "",
    code: "",
    firstActionId: null,
    firstAction: {
      id: "",
      name: "",
    },
    active: true,
    organisationId: "",
    audit: {
      createdByUserId: "",
      createdAtUTC: null,
      lastUpdatedByUserId: "",
      lastUpdatedAtUTC: null,
    },
  },
};

// Thunk to fetch category by ID (for edit mode)
export const fetchCategoryById = createAsyncThunk<CategoriesType, string>(
  "category/fetchCategoryById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get<CategoriesType>(`${API_ENDPOINTS.categories}/${id}`);
      return data;
    } catch (error: any) {
      const errorInfo = ExtractErrorInfo(error);
      return rejectWithValue(errorInfo);
    }
  }
);

interface SaveCategoryParams {
  formData: CategoriesType;
  categoryId?: string;
}

export const saveCategory = createAsyncThunk<CategoriesType, SaveCategoryParams>(
  "category/saveCategory",
  async ({ formData, categoryId }, { rejectWithValue }) => {
    try {
      let response;
      if (categoryId) {
        // If categoryId exists, update (PATCH request)
        response = await api.patch<CategoriesType>(`${API_ENDPOINTS.categories}/${categoryId}`, formData);
        return response.data;
      } else {
        // If categoryId does not exist, create new (POST request)
        response = await api.post<CategoriesType>(`${API_ENDPOINTS.categories}`, formData);
      }
      return response.data;
    } catch (error: any) {
      const errorInfo = ExtractErrorInfo(error);
      return rejectWithValue(errorInfo);
    }
  }
);

export const fetchCategories = createAsyncThunk("category/fetchCategories", async (formData, { dispatch }) => {
  try {
    const response = await api.get<CategoriesType>("IncidentManagement.API/Categories/GetActions");
    return response.data.name;
  } catch (error: any) {
    throw error;
  }
});

const categoriesSlice = createSlice({
  name: "categories",
  initialState,
  reducers: {
    clearForm: (state) => {
      state.categoriesType = initialState.categoriesType;
    },
    clearCategoryError: (state) => {
      return {
        ...state,
        error: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCategoryById.fulfilled, (state, action: PayloadAction<CategoriesType>) => {
      state.categoriesType.name = action.payload.name;
      state.categoriesType.code = action.payload.code;
      state.categoriesType.firstActionId = action.payload.firstActionId;
      state.categoriesType.firstAction = action.payload.firstAction;
      state.categoriesType.active = action.payload.active;
      state.categoriesType.organisationId = action.payload.organisationId;
      state.loading = false;
    });
    builder.addCase(fetchCategoryById.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchCategoryById.rejected, (state, action) => {
      return {
        ...state,
        error: action.payload as ErrorType,
        loading: false,
      };
    });
    builder.addCase(fetchCategories.fulfilled, (state, action: PayloadAction<string>) => {
      // state.firstActions = action.payload;
      return;
    });
    builder.addCase(saveCategory.fulfilled, (state, action) => {
      return {
        ...state,
        error: null,
        loading: false,
        categoriesType: initialState.categoriesType, //reset the form after a successful response.
      };
    });
    builder.addCase(saveCategory.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(saveCategory.rejected, (state, action) => {
      return {
        ...state,
        error: action.payload as ErrorType,
        loading: false,
      };
    });
  },
});

export const { clearForm, clearCategoryError } = categoriesSlice.actions;

export const actionRootState = (state: RootState) => state;
export default categoriesSlice.reducer;
